/**
 * External dependencies
 */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Box, Stack, Heading, Text } from '@towersystems/towersystems-design-system';
import { get } from 'lodash';

/**
 * Internal dependencies
 */
import Card from 'components/card';
import AnnotatedSection from 'components/annotated-section';
import FormInputCheckbox from 'components/forms/form-checkbox';
import FormInputRadio from 'components/forms/form-radio';
import FormTextInput from 'components/forms/form-text-input';
import FormNumberInput from 'components/forms/form-number-input';
import FormLabel from 'components/forms/form-label';

const ShopifyPreferences = ({ saveConfigValue, saveConfigValueInput, hideResults, values }) => {

    return (
        <Stack space='xlarge' dividers>
            <AnnotatedSection>
                <AnnotatedSection.Annotation
                    title='Product'
                    description='Choose your preferences when sending product data.'
                />
                <AnnotatedSection.Content>
                    <Stack space='xlarge'>
                        <Card>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product title'
                                    onChange={saveConfigValue}
                                    name='updateProductName'
                                    checked={get(values, 'updateProductName', false)}
                                >
                                    When ticked the Description from Retailer will update Product Name/Title 
                                    in your ecommerce platform.
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product description'
                                    onChange={saveConfigValue}
                                    name='updateProductDescription'
                                    checked={get(values, 'updateProductDescription', false)}
                                >
                                    When ticked the Web Description from Retailer will be used to update 
                                    Description in your ecommerce platform.
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product tags'
                                    onChange={saveConfigValue}
                                    name='updateProductTags'
                                    checked={get(values, 'updateProductTags', false)}
                                >
                                    When ticked, the list of Product Tags from Retailer and a tag create from the 
                                    Category names will be added to the Tags field in your ecommerce platform.  
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product vendor'
                                    onChange={saveConfigValue}
                                    name='updateProductVendor'
                                    checked={get(values, 'updateProductVendor', false)}
                                >
                                    Tags prefixed with Brand_ will be added to the Vendor field in Shopify. 
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section className='card__section-productType'>
                                <FormInputCheckbox
                                    label='Replace Custom Product Type with Tag'
                                    onChange={saveConfigValue}
                                    name='useProductTypeTagAsProductType'
                                    checked={get(values, 'useProductTypeTagAsProductType', false)}
                                >
                                    Tags prefixed with Type_ will be added to the Custom Product Type field in Shopify. This will replace the
                                    department / category value
                                    where the tag exists. If the Tag does not exist the
                                    Department / Category Type
                                    (if enabled) will continue to be used.
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product weight'
                                    onChange={saveConfigValue}
                                    name='updateProductWeight'
                                    checked={get(values, 'updateProductWeight', false)}
                                >
                                    When ticked the product weight and unit field from Retailer will update the product weight 
                                    filed in your ecommerce platform. 
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product price'
                                    onChange={saveConfigValue}
                                    name='updateProductPrice'
                                    checked={get(values, 'updateProductPrice', false)}
                                >
                                    When ticked the chosen price from Retailer (see inventory and pricing below) 
                                    will be used to update the product price in your ecommerce platform.
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product metafield'
                                    onChange={saveConfigValue}
                                    name='updateMetafield'
                                    checked={get(values, 'updateMetafield', false)}
                                >
                                    When ticked the attribute from Retailer will be used to 
                                    update the product metafield in your ecommerce platform.
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product barcode'
                                    onChange={saveConfigValue}
                                    name='updateProductBarcode'
                                    checked={get(values, 'updateProductBarcode', false)}
                                >
                                    When ticked the Stock Number from Retailer will be used to 
                                    update the Barcode in your ecommerce platform.
                                </FormInputCheckbox>
                            </Card.Section>
                        </Card>
                    </Stack>
                </AnnotatedSection.Content>
            </AnnotatedSection>
            <AnnotatedSection>
                <AnnotatedSection.Annotation
                    title='Department'
                    description='Choose your preferences when sending department data.'
                />
                <AnnotatedSection.Content>
                    <Card>
                        <Card.Section>
                            <FormInputCheckbox
                                label='Update department title'
                                onChange={saveConfigValue}
                                name='updateDepartmentName'
                                checked={get(values, 'updateDepartmentName', false)}
                            >
                                When ticked the Department Name from Retailer will be used to update 
                                the corresponding Collection/Category in your ecommerce platform.
                            </FormInputCheckbox>
                        </Card.Section>
                        { (!hideResults || !get(values, 'useCategoriesSmartCollections')) ?
                        <Card.Section>
                            <FormInputCheckbox
                                label='Department as smart collections'
                                onChange={saveConfigValue}
                                name='useSmartCollections'
                                checked={get(values, 'useSmartCollections', false)}
                            >
                                When ticked this will create any NEW Departments as Smart Collections in Shopify.  
                                When enabled the Departments will also be added to the product type to allow the filters to be created. 
                            </FormInputCheckbox>
                        </Card.Section>
                        : null }
    
                        <Card.Section className='card__section-productType'>
                            <FormInputCheckbox
                                label='Use department as product type'
                                onChange={saveConfigValue}
                                name='useDepartmentAsProductType'
                                checked={get(values, 'useCategoriesSmartCollections', false)?false:(get(values, 'useSmartCollections', false)?true:get(values, 'useDepartmentAsProductType', false))}
                                disabled={get(values, 'useCategoriesSmartCollections', false)?true:false}
                            >
                                When ticked, if you are not using Smart Collections, 
                                you add the Department name to be to the product type field.
                            </FormInputCheckbox>
                        </Card.Section> 
            
                        <Card.Section>
                            <FormInputCheckbox
                                label='Department as Metafields'
                                onChange={saveConfigValue}
                                name='departmentAsMetafields'
                                checked={get(values, 'departmentAsMetafields', false)}
                            >
                                If this option is selected then the Departments will be imported as metafields in Shopify.
                            </FormInputCheckbox>
                        </Card.Section>
                    </Card>
                </AnnotatedSection.Content>
            </AnnotatedSection>
            <AnnotatedSection>
                <AnnotatedSection.Annotation
                    title='Category'
                    description='Choose your preferences when sending category data.'
                />
                <AnnotatedSection.Content>
                    <Card>
                        { (!hideResults || !get(values, 'useSmartCollections')) ?
                        <Card.Section>
                            <FormInputCheckbox
                                label='Category as smart collections'
                                onChange={saveConfigValue}
                                name='useCategoriesSmartCollections'
                                checked={get(values, 'useCategoriesSmartCollections', false)}
                            >
                                When ticked this will create any NEW Categories as Smart Collections in Shopify.  
                                When enabled the Categories will also be added to the product type to allow the filters to be created. 
                            </FormInputCheckbox>
                        </Card.Section>
                        : null }
                        <Card.Section className='card__section-productType'>
                            <FormInputCheckbox
                                label='Use category as product type'
                                onChange={saveConfigValue}
                                name='useCategoryAsProductType'
                                checked={get(values, 'useSmartCollections', false)?false:(get(values, 'useCategoriesSmartCollections', false)?true:get(values, 'useCategoryAsProductType', false))}
                                disabled={get(values, 'useSmartCollections', false)?true:false}
                            >
                                When ticked, if you are not using Smart Collections, 
                                you add the Category name to be to the product type field.
                            </FormInputCheckbox>
                        </Card.Section> 
                        <Card.Section>
                            <FormInputCheckbox
                                label='Prefix Category Tags'
                                onChange={saveConfigValue}
                                name='prefixCatToTag'
                                checked={get(values, 'prefixCatToTag', false)}
                            >
                                If this option is selected then the Category Tags will be prefixed with Category_. 
                                This allows easier designation between product tags and category tags Which is 
                                useful when creating navigation. <br /><br />PLEASE NOTE: IF YOU HAVE AN EXISTING SHOPIFY 
                                SITE ENABLING THIS OPTION MAY BREAK YOUR SITES NAVIGATION. 
                                PLEASE CHECK WITH YOUR SITES DEVELOPERS BEFORE USING THIS OPTION.
                            </FormInputCheckbox>
                        </Card.Section>
                        <Card.Section>
                            <FormInputCheckbox
                                label='Category as Metafields'
                                onChange={saveConfigValue}
                                name='categoryAsMetafields'
                                checked={get(values, 'categoryAsMetafields', false)}
                            >
                                If this option is selected then the Categories will be imported as metafields in Shopify.
                            </FormInputCheckbox>
                        </Card.Section>
                    </Card>
                </AnnotatedSection.Content>
            </AnnotatedSection>
            <AnnotatedSection>
                <AnnotatedSection.Annotation
                    title='Inventory & Pricing'
                    description='Choose your preferences when sending inventory and pricing data.'
                />
                <AnnotatedSection.Content>
                    <Stack space='large'>
                        <Card>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Update product inventory levels'
                                    onChange={saveConfigValue}
                                    name='updateInventory'
                                    checked={get(values, 'updateInventory', false)}
                                >
                                    When ticked, the Stock on Hand value from Retailer will be 
                                    used as the inventory level in your ecommerce platform
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='TWC Track product inventory levels'
                                    onChange={saveConfigValue}
                                    name='trackInventory'
                                    checked={get(values, 'trackInventory', false)}
                                >
                                    When ticked, the Stock on Hand value from Retailer will be subtracted 
                                    by the reported, but not yet imported, TWC sales 
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Round down QOH To Whole Number'
                                    onChange={saveConfigValue}
                                    name='roundDownInventory'
                                    checked={get(values, 'roundDownInventory', false)}
                                >
                                    When ticked, the Stock on Hand value from Retailer will be 
                                    rounded down in your ecommerce platform if they are in decimal
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Use static quantity'
                                    onChange={saveConfigValue}
                                    name='useStaticQuantity'
                                    checked={get(values, 'useStaticQuantity', false)}
                                >
                                    When ticked, if the StaticQuantity received from Retailer is true, the inventory for the product will not be tracked in your ecommerce platform
                                </FormInputCheckbox>
                            </Card.Section>
                        </Card>
                        <Card>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Use web price'
                                    onChange={saveConfigValue}
                                    name='useWebPrice'
                                    checked={get(values, 'useWebPrice', false)}
                                >
                                    When ticked the Retailer Web Price will be used as the sell price of the item.  
                                    If unticked the system will use the Retailer Retail Price.  
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Use catalogue price'
                                    onChange={saveConfigValue}
                                    name='useCataloguePrice'
                                    checked={get(values, 'useCataloguePrice', false)}
                                >
                                    When ticked, the Retailer Stock Item Catalogue price will be used in Shopify 
                                    as the Retail Price.  The regular price, either the web or retail price, will be set as the compare at price.
                                </FormInputCheckbox>
                            </Card.Section>
                        </Card>
                        <Card>
                            <Card.Section>
                                <Box className="card__section-outofstockqty">
                                    <FormLabel>Out of Stock Quantity:</FormLabel>
                                    <FormNumberInput name='outOfStockQty' onChange={saveConfigValueInput} value={get(values, 'outOfStockQty')} />
                                    <Text className="form-text-input__additional-content">The Quantity entered here will be used as 
                                    out of stock quantity. Any qty below this value will be marked as out of stock on your ecommerce platform.</Text>
                                </Box>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Unpublish out of stock'
                                    onChange={saveConfigValue}
                                    name='unpublishOutOfStock'
                                    checked={get(values, 'unpublishOutOfStock', false)}
                                >
                                    When ticked, products with chosen inventory will be unpublished or 
                                    unavailable for purchase on your ecommerce platform.
                                </FormInputCheckbox>
                            </Card.Section>
                            <Card.Section>
                                <FormInputCheckbox
                                    label='Unpublish out of stock products from Sales Channels'
                                    onChange={saveConfigValue}
                                    name='unpublishOutOfStockFromSalesChannels'
                                    checked={get(values, 'unpublishOutOfStockFromSalesChannels', false)}
                                >
                                    When ticked, products with out of stock quantity will be unpublished from all available Sales Channels.<br /><br />
                                    PLEASE NOTE: IN THE EVENT THAT ITEMS ARE RE-PUBLISHED ONLY THE ONLINE STORE CHANNEL WILL BE SELECTED.
                                </FormInputCheckbox>
                            </Card.Section>
                        </Card>
                    </Stack>
                </AnnotatedSection.Content>
            </AnnotatedSection>

            <AnnotatedSection>
                <AnnotatedSection.Annotation
                    title='Blog Posts'
                    description='Choose your preferences when sending blog posts data.'
                />
                <AnnotatedSection.Content>
                    <Card>
                        <Card.Section>
                            <FormInputCheckbox
                                label='Send Blog Posts'
                                onChange={saveConfigValue}
                                name='sendBlogPosts'
                                checked={get(values, 'sendBlogPosts', false)}
                            >
                                When ticked, Blog posts from Retailer will be sent to your ecommerce platform.
                            </FormInputCheckbox>
                        </Card.Section>

                    </Card>
                </AnnotatedSection.Content>
            </AnnotatedSection>
        </Stack>
    );
};

export default connect((state, ownProps) => ({}), {})(ShopifyPreferences);
